import styled, { css } from 'styled-components';
import { FovTypography } from 'components/typography';
import { getColor } from 'utils';
import {
  motion,
  cardEnter,
  cardFade,
  nodePulse,
  haloPulse,
  chevronRise,
  driftUp,
  queueAdvance,
  cubeSpin,
  drawEdges,
  shimmer,
  sheen,
  drawCheck,
  ringExpand,
  errorPop,
  errorShake,
} from './animations';

// The routes may be embedded in a customer iframe, so the page fills whatever
// box it is given and scrolls inside it rather than assuming a viewport height.
export const StatusPage = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 1.5rem 1rem;
  background-color: ${props => getColor(props, 'primary')};
  color: ${props => getColor(props, 'onPrimary')};
`;

export const Card = styled(FovTypography)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 28rem;
  padding: 2rem;
  border-radius: 0.35rem;
  color: ${props => getColor(props, 'onPrimary')};
  animation: ${cardFade} 250ms ease-out both;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${cardEnter} 250ms ease-out both;
  }

  @media (max-width: 24rem) {
    padding: 1.25rem 0.75rem;
  }
`;

export const ModelTitle = styled(FovTypography)`
  font-size: ${props => props.theme.headings.md};
  font-family: ${props => props.theme.typography.primary.bold};
  color: ${props => getColor(props, 'onPrimary')};
  overflow-wrap: anywhere;
`;

export const Headline = styled(FovTypography)`
  font-size: clamp(1.5rem, 5vw, ${props => props.theme.headings.lg});
  line-height: 1.15;
  color: ${props => getColor(props, 'onPrimary')};
  overflow-wrap: anywhere;
`;

export const SubLine = styled(FovTypography)`
  font-size: ${props => props.theme.headings.md};
  line-height: 1.4;
  color: ${props => getColor(props, 'darkGrey')};
`;

export const MutedLine = styled(FovTypography)`
  font-size: ${props => props.theme.headings.sm};
  color: ${props => getColor(props, 'darkGrey')};
`;

export const Actions = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  width: 100%;
  max-width: 16rem;
`;

// Outbound links never navigate the embedding page away from the viewer.
export const SupportButton = styled('a')`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.75rem;
  padding: 0 1rem;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.25rem;
  background-color: ${props => getColor(props, 'transparent')};
  color: ${props => getColor(props, 'onPrimary')};
  font-size: 0.9rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => getColor(props, 'accent')};
    color: ${props => getColor(props, 'accent')};
  }
`;

export const ButtonContent = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

/* ------------------------------------------------------------------ art --- */

// The stage reserves its box whether or not the thumbnail ever loads, so a
// late-arriving (or missing) preview never shifts the layout underneath it.
export const ArtStage = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 11rem;

  @media (max-width: 24rem) {
    height: 4.5rem;
  }
`;

export const ArtScale = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 11rem;
  height: 11rem;
  transform-origin: center;

  @media (max-width: 24rem) {
    transform: scale(0.4);
  }
`;

export const ThumbFrame = styled('div')`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border: 0.0625rem solid ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
`;

export const Thumb = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0.4;
`;

export const Sheen = styled('div')`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 35%;
  opacity: 0;
  background: linear-gradient(
    90deg,
    ${props => getColor(props, 'transparent')},
    ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.35)},
    ${props => getColor(props, 'transparent')}
  );

  @media (prefers-reduced-motion: no-preference) {
    opacity: 1;
    animation: ${sheen} 4.5s ease-in-out infinite;
  }
`;

export const ArtLayer = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* --------------------------------------------------------- progress rail --- */

export const Rail = styled('div')`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  max-width: 20rem;
`;

export const RailTrack = styled('div')`
  position: absolute;
  top: 0.4375rem;
  left: 12.5%;
  right: 12.5%;
  height: 0.125rem;
  background-color: ${props => getColor(props, 'mediumGrey')};
`;

export const RailTrail = styled('div')`
  height: 100%;
  width: ${props => props.$fill}%;
  background-color: ${props => getColor(props, props.$frozen ? 'error' : 'accent')};
  transition: width 400ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const RailStop = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
`;

const nodeColor = props => {
  if (props.$frozen && (props.$done || props.$active)) return 'error';
  if (props.$done || props.$active) return 'accent';
  return 'mediumGrey';
};

export const RailNode = styled('div')`
  position: relative;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: ${props => getColor(props, nodeColor(props))};

  ${props => props.$active && !props.$frozen && motion(css`${nodePulse} 1.6s ease-in-out infinite`)}
`;

export const RailHalo = styled('div')`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  opacity: 0;
  background-color: ${props => getColor(props, 'accent')};

  ${motion(css`${haloPulse} 1.6s ease-in-out infinite`)}
`;

export const RailLabel = styled(FovTypography)`
  font-size: ${props => props.theme.headings.xs};
  color: ${props => getColor(props, props.$active ? 'onPrimary' : 'darkGrey')};

  @media (max-width: 24rem) {
    display: none;
  }
`;

/* ------------------------------------------------- waiting for file / art --- */

export const Tray = styled('div')`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 5.5rem;
  height: 3.25rem;
  border: 0.125rem dashed ${props => getColor(props, 'mediumGrey')};
  border-radius: 0.5rem;
`;

export const Chevron = styled('div')`
  position: absolute;
  top: -1.5rem;
  display: flex;
  color: ${props => getColor(props, 'accent')};

  ${motion(css`${chevronRise} 2s ease-in-out infinite`)}
`;

export const DriftDot = styled('div')`
  position: absolute;
  bottom: 0.75rem;
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background-color: ${props => getColor(props, 'accent')};
  transform: translateX(${props => props.$offset}rem);
  opacity: 0.5;

  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    animation: ${driftUp} 2s ease-in-out ${props => props.$delay}ms infinite;
  }
`;

/* --------------------------------------------------------------- queued --- */

export const QueueTrack = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 9rem;
  height: 2.5rem;
  overflow: hidden;
`;

export const Pill = styled('div')`
  width: 1.75rem;
  height: 0.75rem;
  border-radius: 0.375rem;
  background-color: ${props => getColor(props, props.$leading ? 'accent' : 'mediumGrey')};

  @media (prefers-reduced-motion: no-preference) {
    position: absolute;
    animation: ${queueAdvance} 2.4s linear ${props => props.$delay}ms infinite;
  }
`;

/* ----------------------------------------------------------- processing --- */

export const CubeStage = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6rem;
  height: 6rem;
  perspective: 20rem;
`;

export const Cube = styled('svg')`
  color: ${props => getColor(props, 'accent')};
  transform-style: preserve-3d;

  ${motion(css`${cubeSpin} 8s linear infinite`)}
`;

export const CubeEdges = styled('g')`
  stroke-dasharray: 260;
  stroke-dashoffset: 0;

  ${motion(css`${drawEdges} 3s ease-in-out infinite`)}
`;

// Deliberately indeterminate — the jobs API reports no progress percentage.
export const ShimmerTrack = styled('div')`
  position: relative;
  width: 100%;
  max-width: 16rem;
  height: 0.25rem;
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: ${props => getColor(props, 'mediumGrey')};
`;

export const ShimmerBar = styled('div')`
  width: 30%;
  height: 100%;
  border-radius: 0.25rem;
  background: linear-gradient(
    90deg,
    ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.2)},
    ${props => getColor(props, 'accent')},
    ${props => props.theme.hexToRgba(getColor(props, 'accent'), 0.2)}
  );

  ${motion(css`${shimmer} 1.8s ease-in-out infinite`)}
`;

/* ------------------------------------------------------------ completed --- */

export const CheckMark = styled('svg')`
  color: ${props => getColor(props, 'success')};
`;

export const CheckPath = styled('path')`
  stroke-dasharray: 48;
  stroke-dashoffset: 0;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${drawCheck} 500ms ease-out both;
  }
`;

export const SuccessRing = styled('div')`
  position: absolute;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  opacity: 0;
  border: 0.125rem solid ${props => getColor(props, 'success')};

  @media (prefers-reduced-motion: no-preference) {
    animation: ${ringExpand} 900ms ease-out both;
  }
`;

/* ------------------------------------------------------------- terminal --- */

export const ErrorMark = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  color: ${props => getColor(props, 'error')};
  border: 0.125rem solid ${props => getColor(props, 'error')};

  @media (prefers-reduced-motion: no-preference) {
    animation:
      ${errorPop} 300ms ease-out both,
      ${errorShake} 220ms ease-in-out 300ms 2;
  }
`;
